import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  BuildingLibraryIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  TagIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { financeiroApi } from '../../../services/api';
import type { DashboardFinanceiroData } from '../../../types';

const DashboardFinanceiro: React.FC = () => {
  const [data, setData] = useState<DashboardFinanceiroData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        const dashboardData = await financeiroApi.getDashboard();
        setData(dashboardData);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar dashboard financeiro');
        console.error('Error loading dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
    <div className="space-y-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
    <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">
                {error || 'Erro ao carregar dados do dashboard'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Receitas',
      value: data.total_receitas,
      icon: CurrencyDollarIcon,
      color: 'bg-green-50',
      textColor: 'text-green-700',
      borderColor: 'border-green-200'
    },
    {
      label: 'Total Despesas',
      value: data.total_despesas,
      icon: CurrencyDollarIcon,
      color: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200'
    },
    {
      label: 'Saldo',
      value: data.saldo,
      icon: BuildingLibraryIcon,
      color: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200'
    },
    {
      label: 'Vencidos',
      value: data.vencidos,
      icon: ExclamationCircleIcon,
      color: data.vencidos > 0 ? 'bg-red-50' : 'bg-gray-50',
      textColor: data.vencidos > 0 ? 'text-red-700' : 'text-gray-700',
      borderColor: data.vencidos > 0 ? 'border-red-200' : 'border-gray-200'
    }
  ];

  return (
    <div className="space-y-4">
      <div className="mb-8">
        <h1 className="text-base font-bold text-gray-900">Dashboard Financeiro</h1>
        <p className="mt-0.5 text-xs text-gray-700">
          Visão geral da situação financeira da empresa
        </p>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
          <div className="flex">
            <ExclamationCircleIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-yellow-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className={`${card.color} border ${card.borderColor} rounded-lg p-6 shadow-sm`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-600">{card.label}</p>
                  <p className={`text-2xl font-bold ${card.textColor} mt-2`}>
                    {formatCurrency(card.value)}
                  </p>
                </div>
                <Icon className={`h-8 w-8 ${card.textColor} opacity-75`} />
              </div>
              {card.label === 'Vencidos' && card.value > 0 && (
                <div className="mt-2">
                  <span className="inline-block bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    {card.value} vencimento(s)
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Access */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-sm font-bold text-gray-900 mb-4">Acesso Rápido</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Link
            to="/financeiro/lancamentos"
            className="flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
          >
            <div className="flex items-center">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-3" />
              <div>
                <p className="text-xs font-medium text-gray-900">Lançamentos</p>
                <p className="text-xs text-gray-600">Contas a pagar/receber</p>
              </div>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-blue-600" />
          </Link>

          <Link
            to="/financeiro/contas"
            className="flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors"
          >
            <div className="flex items-center">
              <BuildingLibraryIcon className="h-6 w-6 text-green-600 mr-3" />
              <div>
                <p className="text-xs font-medium text-gray-900">Contas Bancárias</p>
                <p className="text-xs text-gray-600">Gerenciar contas</p>
              </div>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-green-600" />
          </Link>

          <Link
            to="/financeiro/categorias"
            className="flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors"
          >
            <div className="flex items-center">
              <TagIcon className="h-6 w-6 text-purple-600 mr-3" />
              <div>
                <p className="text-xs font-medium text-gray-900">Categorias</p>
                <p className="text-xs text-gray-600">Organizar por categoria</p>
              </div>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-purple-600" />
          </Link>

          <Link
            to="/financeiro/beneficiarios"
            className="flex items-center justify-between p-4 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg transition-colors"
          >
            <div className="flex items-center">
              <UserGroupIcon className="h-6 w-6 text-orange-600 mr-3" />
              <div>
                <p className="text-xs font-medium text-gray-900">Beneficiários</p>
                <p className="text-xs text-gray-600">Fornecedores e clientes</p>
              </div>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-orange-600" />
          </Link>

          <Link
            to="/financeiro/relatorios/fluxo-caixa"
            className="flex items-center justify-between p-4 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors"
          >
            <div className="flex items-center">
              <ChartBarIcon className="h-6 w-6 text-teal-600 mr-3" />
              <div>
                <p className="text-xs font-medium text-gray-900">Fluxo de Caixa</p>
                <p className="text-xs text-gray-600">Movimentação financeira</p>
              </div>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-teal-600" />
          </Link>

          <Link
            to="/financeiro/relatorios/dre"
            className="flex items-center justify-between p-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors"
          >
            <div className="flex items-center">
              <ChartBarIcon className="h-6 w-6 text-indigo-600 mr-3" />
              <div>
                <p className="text-xs font-medium text-gray-900">DRE</p>
                <p className="text-xs text-gray-600">Demonstração de resultado</p>
              </div>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-indigo-600" />
          </Link>
        </div>
      </div>

      {/* Bank Accounts */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-sm font-bold text-gray-900 mb-4">Contas Bancárias</h2>

        {data.contas && data.contas.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Banco
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agência / Conta
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Saldo Atual
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {data.contas.map((conta) => (
                  <tr key={conta.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <BuildingLibraryIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-xs font-medium text-gray-900">{conta.nome}</p>
                          <p className="text-xs text-gray-500">{conta.banco}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{conta.tipo}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {conta.agencia && conta.conta ? `${conta.agencia} / ${conta.conta}` : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(conta.saldo_atual)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {conta.ativa ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Ativa
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                          Inativa
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <BuildingLibraryIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-0.5 text-xs text-gray-500">Nenhuma conta bancária cadastrada</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardFinanceiro;
